import { Button, Modal, Select, Text } from "@mantine/core";
import * as Sentry from "@sentry/browser";
import { useEffect, useState } from "react";
import api from "../../../api";
import { useSystemCreatedTodos } from "../../../hooks/todos/useSystemCreatedTodos";
import { ModalFooter } from "../../../mantine/components/ModalFooter/ModalFooter";
import { useToast } from "../../../mantine/notifications/useToast";
import urls from "../../../urls";
import type { TaskStatusResponse } from "../../../utils/api-utils";
import { pollTaskStatus } from "../../../utils/api-utils";
import type { ExtendedUser, Todo } from "../../../utils/backend-types";
import { TodoStatus } from "../../../utils/backend-types";
import { FINAL_TODO_YEAR, INITIAL_TODO_YEAR } from "../../../utils/dates";
import { backendDateOrDateTimeToLuxonDateTime } from "../../../utils/dates/backendDateOrDateTimeToLuxonDateTime";
import { LoadOrError } from "../../LoadOrError/LoadOrError";
import { TodoDecisionWidget } from "../TodoDecisionWidget/TodoDecisionWidget";
import { TodoSystemDecision, TodoUserDecision } from "../Todos.types";

const DEFAULT_USER_DECISION_BY_SYSTEM_DECISION = {
  [TodoSystemDecision.Applicable]: TodoUserDecision.Confirmed,
  [TodoSystemDecision.NotApplicable]: TodoUserDecision.Rejected,
  [TodoSystemDecision.CantTell]: TodoUserDecision.Unclear,
  [TodoSystemDecision.NeedMoreInformation]: TodoUserDecision.Unclear
};

const YEAR_LABEL =
  "Bitte geben Sie das Meldejahr an, für welches die Prüfung der Aufgaben erfolgen soll.*";
const INITIAL_YEAR = INITIAL_TODO_YEAR;
const EARLIEST_YEAR = 2020;
const YEAR_CHOICES = Array(FINAL_TODO_YEAR - EARLIEST_YEAR + 1)
  .fill("")
  .map((_, index) => ({
    value: String(FINAL_TODO_YEAR - index),
    label: (FINAL_TODO_YEAR - index).toString()
  }));

interface AutoFillTodoModalProps {
  isOpen: boolean;
  variantId: number;
  users: Array<ExtendedUser>;
  onToggle: () => void;
  onRequireTodoRefresh: () => void;
}

function AutoFillTodoModal({
  isOpen,
  variantId,
  users,
  onToggle,
  onRequireTodoRefresh
}: AutoFillTodoModalProps) {
  const [showTodoDecisionWidget, setShowTodoDecisionWidget] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submittingTodos, setSubmittingTodos] = useState(false);
  const [todos, setTodos] = useState<Array<Todo>>([]);
  const [userDecisions, setUserDecisions] = useState<
    Record<number, TodoUserDecision>
  >({});
  const [finishedTodoIds, setFinishedTodoIds] = useState<Array<number>>([]);
  const [year, setYear] = useState(INITIAL_YEAR);
  const { showErrorToast } = useToast();

  const {
    data: systemCreatedTodos,
    error: systemCreatedTodosError,
    isLoading: systemCreatedTodosLoading
  } = useSystemCreatedTodos(variantId, showTodoDecisionWidget);

  useEffect(() => {
    if (showTodoDecisionWidget && systemCreatedTodos) {
      updateTodosAndUserDecisionsFromResponseData(systemCreatedTodos);
    }
  }, [showTodoDecisionWidget, systemCreatedTodos]);

  function updateTodosAndUserDecisionsFromResponseData(
    responseTodos: Array<Todo>
  ) {
    const unconfirmedTodos = responseTodos.filter(
      (todo) => todo.userDecision !== TodoUserDecision.Confirmed
    );
    const currentOrDefaultUserDecisions = responseTodos.reduce(
      (decisions: Record<number, TodoUserDecision>, todo) => {
        if (todo.userDecision === TodoUserDecision.Open) {
          if (todo.systemDecision) {
            const defaultUserDecision =
              DEFAULT_USER_DECISION_BY_SYSTEM_DECISION[todo.systemDecision];
            decisions[todo.id] = defaultUserDecision;
          } else {
            Sentry.captureMessage(
              `System decision wasn't set for todo "${todo.label}" (ID ${todo.id}).`
            );
            decisions[todo.id] = TodoUserDecision.Unclear;
          }
        } else {
          decisions[todo.id] = todo.userDecision;
        }

        return decisions;
      },
      {}
    );

    setTodos(unconfirmedTodos);
    setUserDecisions(currentOrDefaultUserDecisions);
  }

  function handleChangeUserDecisionAndFinished(
    todoId: number,
    userDecision: TodoUserDecision,
    finished: boolean
  ) {
    let newFinishedTodoIds: Array<number>;

    if (userDecision === TodoUserDecision.Confirmed && finished) {
      if (!finishedTodoIds.includes(todoId)) {
        newFinishedTodoIds = [...finishedTodoIds, todoId];
      } else {
        newFinishedTodoIds = finishedTodoIds;
      }
    } else {
      newFinishedTodoIds = finishedTodoIds.filter(
        (finishedTodoId) => finishedTodoId !== todoId
      );
    }

    setUserDecisions({
      ...userDecisions,
      [todoId]: userDecision
    });
    setFinishedTodoIds(newFinishedTodoIds);
  }

  function handleSave() {
    const decisionTodoIds = Object.keys(userDecisions).map((todoId) =>
      parseInt(todoId, 10)
    );
    const changedDecisionTodoIds = decisionTodoIds.filter((todoId) => {
      const originalTodoData = todos.find((todo) => todo.id === todoId);

      if (
        !originalTodoData ||
        originalTodoData.userDecision === userDecisions[todoId]
      ) {
        return false;
      }

      return true;
    });

    setSubmittingTodos(true);

    api
      .post(urls.api.updateTodoUserDecisions(), {
        todos: changedDecisionTodoIds.map((todoId) => ({
          id: todoId,
          userDecision: userDecisions[todoId]
        }))
      })
      .then(() => {
        // these calls are made after the decision updates because of a backend bug where the status updates are lost
        // if they are made before the decision updates have returned
        return Promise.all(
          finishedTodoIds.map((todoId) => {
            return api.post(urls.api.updateTodoStatus(todoId), {
              status: TodoStatus.Done
            });
          })
        );
      })
      .then(() => {
        onRequireTodoRefresh();
        onToggle();
      })
      .catch((error) => {
        showErrorToast(error);
      })
      .finally(() => setSubmittingTodos(false));
  }

  function handleDone() {
    setShowTodoDecisionWidget(true);
    setSubmitting(false);
  }

  function handleSubmitError(error: unknown) {
    showErrorToast(error);
    setSubmitting(false);
  }

  async function handleSubmit() {
    setSubmitting(true);
    try {
      const response = await api.post<TaskStatusResponse>(
        urls.api.todosUpdateList(),
        { year, variant: variantId }
      );
      const taskStatusUrl = response.data.taskStatusUrl;

      if (taskStatusUrl) {
        pollTaskStatus(taskStatusUrl, handleDone, handleSubmitError);
      } else {
        handleDone();
      }
    } catch (error) {
      handleSubmitError(error);
    }
  }

  function handleClickBack() {
    setShowTodoDecisionWidget(false);
    setSubmitting(false);
    setTodos([]);
    setUserDecisions({});
    setFinishedTodoIds([]);
    setYear(INITIAL_YEAR);
  }

  function handleClose() {
    onToggle();
  }

  const todosDueNextYear = todos.filter((todo) =>
    todo.dueDate
      ? backendDateOrDateTimeToLuxonDateTime(todo.dueDate).year === year + 1
      : true
  );

  return (
    <Modal
      className="AutoFillTodoModal"
      opened={isOpen}
      size="xl"
      title={`Aufgaben ${year} automatisch prüfen`}
      onClose={handleClose}
    >
      {showTodoDecisionWidget ? (
        <LoadOrError
          error={systemCreatedTodosError}
          loading={systemCreatedTodosLoading}
        >
          {
            <TodoDecisionWidget
              finishedTodoIds={finishedTodoIds}
              todos={todosDueNextYear}
              userDecisions={userDecisions}
              users={users}
              onChangeUserDecisionAndFinished={
                handleChangeUserDecisionAndFinished
              }
            />
          }
        </LoadOrError>
      ) : (
        <>
          <Text mb="xl">
            Auf Basis Ihrer Projekt-Konfiguration wird automatisch überprüft,
            welche Aufgaben erfüllt werden müssen. Während dieser Prüfung werden
            Sie möglicherweise nach weiteren Informationen gefragt.
          </Text>
          <Select
            data={YEAR_CHOICES}
            label={YEAR_LABEL}
            value={String(year)}
            onChange={(v) => setYear(Number(v ?? year))}
          />
        </>
      )}
      <ModalFooter>
        {showTodoDecisionWidget && (
          <Button variant="default" onClick={handleClickBack}>
            Zurück
          </Button>
        )}
        <Button variant="default" onClick={handleClose}>
          Abbrechen
        </Button>
        {!showTodoDecisionWidget && (
          <Button color="secondary" loading={submitting} onClick={handleSubmit}>
            Automatisch prüfen
          </Button>
        )}
        {showTodoDecisionWidget && (
          <Button
            color="secondary"
            loading={submittingTodos}
            onClick={handleSave}
          >
            Auswahl übernehmen
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
}

export { AutoFillTodoModal, AutoFillTodoModalProps };
